/**
 * Waits for one file to finish being checked, then tells the cart to re-read.
 *
 * WHY THIS IS A COMPONENT AND NOT A LOOP. `useUploadReport` is a hook, and a
 * hook cannot be called once per row of a list whose length changes. Rendering
 * one of these per unvalidated row is the only legal way to get a hook per
 * row; it draws nothing.
 *
 * WHY IT POLLS AT ALL. A document over the page threshold is validated in the
 * background — and EVERY Word document is, whatever its size, because it has
 * to be converted to PDF first. So "still checking" is the normal first state
 * of a `.docx`, not an edge case. `useUploadReports` (plural) deliberately
 * does not poll, which is exactly why it cannot do this job.
 *
 * It reports COMPLETION, not the report itself. Everything the row renders —
 * page count, verdict, the warning prose — comes back on the cart item, so
 * the only thing worth knowing here is when to ask the server again.
 */
import { useEffect } from 'react';

import { useUploadReport } from '@/features/validation/useUploadReport';

export interface RowValidationPollerProps {
  uploadId: string;
  /** Fired once, when the file stops being "still checking". */
  onSettled: () => void;
  /** Fired if the poller gives up before an answer arrives. */
  onGaveUp?: () => void;
}

export function RowValidationPoller({ uploadId, onSettled, onGaveUp }: RowValidationPollerProps) {
  const { report, gaveUp } = useUploadReport(uploadId);

  useEffect(() => {
    if (report) onSettled();
    // `onSettled` is deliberately not a dependency: it is a fresh closure on
    // every render of the parent, and depending on it would re-fire the
    // refetch on every render rather than once when the report lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  useEffect(() => {
    if (gaveUp) onGaveUp?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaveUp]);

  return null;
}
